var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import * as pc from "playcanvas";
import { SceneEngine } from "@animech/platform";
import { fitDistance, viewDirection } from "./thumbnail-framing";
import { trimModelName } from "3d/helpers";
import { ProgressiveAA } from "3d/progressive-aa";
export var thumbnailSizePx = 512;
var thumbnailMimeType = "image/webp";
var thumbnailQuality = 0.95;
/* Multisampling smooths silhouettes; supersampling the still model with
 * this many sub-pixel-jittered frames also settles thin threads and specular
 * highlights. Offline we can afford more than the live scene's 8. */
var antiAliasingSamples = 16;
var antiAliasingTimeoutMs = 10000;
var cameraEntityName = "Camera";
/* The scene's only geometry besides the products: a backdrop sphere that would
 * otherwise fill the transparent background. */
var backgroundEntityName = "SphereBG";
/* Textures load behind their materials, which load behind the model, so a
 * single "loaded" event is not enough. The registry has to stay idle for a few
 * checks in a row before the model counts as complete. */
var assetSettleChecks = 3;
var assetSettleIntervalMs = 50;
var sleep = function (ms) {
    return new Promise(function (resolve) { return setTimeout(resolve, ms); });
};
var nextFrame = function () {
    return new Promise(function (resolve) { return requestAnimationFrame(function () { return resolve(); }); });
};
/**
 * Renders single product models to transparent square images using the real
 * PlayCanvas project, so thumbnails get the same materials and lighting as the
 * models in the scene. Used by the dev-only thumbnail generator page.
 */
var ThumbnailRenderer = /** @class */ (function () {
    function ThumbnailRenderer(app, canvas, size) {
        this.app = app;
        this.canvas = canvas;
        this.queue = Promise.resolve();
        app.setCanvasFillMode(pc.FILLMODE_NONE);
        app.setCanvasResolution(pc.RESOLUTION_FIXED, size, size);
        var background = app.root.findByName(backgroundEntityName);
        if (background)
            background.enabled = false;
        /* The scene engine stops the skybox from being redrawn, but the HDRI is
         * still drawn to its own layer. Taking that layer off the camera leaves
         * the HDRI as lighting only, over a transparent clear colour. */
        var camera = this.camera;
        camera.layers = camera.layers.filter(function (layerId) { return layerId !== pc.LAYERID_SKYBOX; });
        camera.clearColor = new pc.Color(0, 0, 0, 0);
    }
    /** Boots the PlayCanvas project on `canvas` with a transparent, readable back buffer. */
    ThumbnailRenderer.create = function (canvas, size) {
        if (size === void 0) { size = thumbnailSizePx; }
        return __awaiter(this, void 0, Promise, function () {
            var sceneEngine;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        sceneEngine = new SceneEngine();
                        return [4 /*yield*/, sceneEngine.init(canvas, "playcanvas/config.json", "1369166.json", {
                                assetPrefix: "playcanvas/",
                                contextOptions: {
                                    alpha: true,
                                    preserveDrawingBuffer: true,
                                    antialias: true,
                                },
                            })];
                    case 1:
                        _a.sent();
                        return [2 /*return*/, new ThumbnailRenderer(sceneEngine.Application, canvas, size)];
                }
            });
        });
    };
    Object.defineProperty(ThumbnailRenderer.prototype, "camera", {
        get: function () {
            var cameraEntity = this.app.root.findByName(cameraEntityName);
            if (!(cameraEntity === null || cameraEntity === void 0 ? void 0 : cameraEntity.camera)) {
                throw new Error("The scene has no \"".concat(cameraEntityName, "\" entity with a camera component"));
            }
            return cameraEntity.camera;
        },
        enumerable: false,
        configurable: true
    });
    /** Renders one job and resolves with the image as a data URL. Jobs run one at a time. */
    ThumbnailRenderer.prototype.render = function (job) {
        var _this = this;
        var run = this.queue.then(function () { return _this.renderJob(job); });
        this.queue = run.catch(function () { return undefined; });
        return run;
    };
    ThumbnailRenderer.prototype.renderJob = function (job) {
        var _a;
        return __awaiter(this, void 0, Promise, function () {
            var asset, entity, antiAliasing, dataUrl;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        (_a = this.modelEntity) === null || _a === void 0 ? void 0 : _a.destroy();
                        this.modelEntity = undefined;
                        asset = this.findModelAsset(job.modelName);
                        if (!asset) {
                            throw new Error("There is no model asset named \"".concat(job.modelName, "\" in the PlayCanvas project"));
                        }
                        return [4 /*yield*/, this.loadAsset(asset)];
                    case 1:
                        _b.sent();
                        entity = new pc.Entity("Thumbnail-".concat(job.modelName));
                        entity.addComponent("model", { type: "asset", asset: asset });
                        this.app.root.addChild(entity);
                        this.modelEntity = entity;
                        return [4 /*yield*/, this.swapMaterials(entity, job.materialSwaps)];
                    case 2:
                        _b.sent();
                        return [4 /*yield*/, this.waitForAssetsToSettle()];
                    case 3:
                        _b.sent();
                        this.frame(entity, job.view);
                        return [4 /*yield*/, nextFrame()];
                    case 4:
                        _b.sent();
                        return [4 /*yield*/, nextFrame()];
                    case 5:
                        _b.sent();
                        return [4 /*yield*/, this.accumulateAntiAliasing()];
                    case 6:
                        antiAliasing = _b.sent();
                        try {
                            dataUrl = this.canvas.toDataURL(thumbnailMimeType, thumbnailQuality);
                            if (!dataUrl.startsWith("data:".concat(thumbnailMimeType))) {
                                throw new Error("This browser cannot encode ".concat(thumbnailMimeType));
                            }
                            return [2 /*return*/, dataUrl];
                        }
                        finally {
                            antiAliasing.destroy();
                            this.app.autoRender = true;
                        }
                        return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Supersamples the still model. `ProgressiveAA` starts accumulating when
     * continuous rendering stops, so pause it and wait for the clean final
     * frame. The caller destroys the returned instance once it has read the
     * canvas, which also resumes continuous rendering.
     */
    ThumbnailRenderer.prototype.accumulateAntiAliasing = function () {
        return __awaiter(this, void 0, Promise, function () {
            var antiAliasing, completed, error_1;
            var _this = this;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        this.app.autoRender = true;
                        completed = new Promise(function (resolve, reject) {
                            var timeout = setTimeout(function () {
                                return reject(new Error("Anti-aliasing did not finish within ".concat(antiAliasingTimeoutMs, "ms")));
                            }, antiAliasingTimeoutMs);
                            antiAliasing = new ProgressiveAA(_this.app, _this.camera, {
                                samples: antiAliasingSamples,
                                onComplete: function () {
                                    clearTimeout(timeout);
                                    resolve();
                                },
                            });
                        });
                        /* The accumulation starts on the first frame after `autoRender` turns
                         * off, and only if the effect has seen a frame with it on. */
                        return [4 /*yield*/, nextFrame()];
                    case 1:
                        /* The accumulation starts on the first frame after `autoRender` turns
                         * off, and only if the effect has seen a frame with it on. */
                        _a.sent();
                        this.app.autoRender = false;
                        _a.label = 2;
                    case 2:
                        _a.trys.push([2, 4, , 5]);
                        return [4 /*yield*/, completed];
                    case 3:
                        _a.sent();
                        return [3 /*break*/, 5];
                    case 4:
                        error_1 = _a.sent();
                        antiAliasing === null || antiAliasing === void 0 ? void 0 : antiAliasing.destroy();
                        this.app.autoRender = true;
                        throw error_1;
                    case 5: return [2 /*return*/, antiAliasing];
                }
            });
        });
    };
    /* Same rule as the `modelAssets` lookup in `canvas.tsx`. */
    ThumbnailRenderer.prototype.findModelAsset = function (modelName) {
        return this.app.assets.filter(function (asset) {
            return asset.type === "model" &&
                !asset.name.toLowerCase().endsWith(".model") &&
                trimModelName(asset.name) === modelName;
        })[0];
    };
    ThumbnailRenderer.prototype.loadAsset = function (asset) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            if (asset.loaded) {
                resolve();
                return;
            }
            asset.ready(function () { return resolve(); });
            asset.once("error", function (error) { return reject(error); });
            _this.app.assets.load(asset);
        });
    };
    ThumbnailRenderer.prototype.swapMaterials = function (entity, materialSwaps) {
        var _a;
        return __awaiter(this, void 0, Promise, function () {
            var _loop_1, this_1, _b, _c, _d, areaName, materialName, e_1_1;
            var e_1, _e;
            return __generator(this, function (_f) {
                switch (_f.label) {
                    case 0:
                        _loop_1 = function (areaName, materialName) {
                            var materialAsset;
                            return __generator(this, function (_g) {
                                switch (_g.label) {
                                    case 0:
                                        materialAsset = this_1.app.assets.find(materialName, "material");
                                        if (!materialAsset) {
                                            throw new Error("There is no material asset named \"".concat(materialName, "\" in the PlayCanvas project"));
                                        }
                                        return [4 /*yield*/, this_1.loadAsset(materialAsset)];
                                    case 1:
                                        _g.sent();
                                        (_a = entity.model) === null || _a === void 0 ? void 0 : _a.meshInstances.forEach(function (meshInstance) {
                                            if (meshInstance.material.name === areaName) {
                                                meshInstance.material = materialAsset.resource;
                                            }
                                        });
                                        return [2 /*return*/];
                                }
                            });
                        };
                        this_1 = this;
                        _f.label = 1;
                    case 1:
                        _f.trys.push([1, 6, 7, 8]);
                        _b = __values(Object.entries(materialSwaps)), _c = _b.next();
                        _f.label = 2;
                    case 2:
                        if (!!_c.done) return [3 /*break*/, 5];
                        _d = __read(_c.value, 2), areaName = _d[0], materialName = _d[1];
                        return [5 /*yield**/, _loop_1(areaName, materialName)];
                    case 3:
                        _f.sent();
                        _f.label = 4;
                    case 4:
                        _c = _b.next();
                        return [3 /*break*/, 2];
                    case 5: return [3 /*break*/, 8];
                    case 6:
                        e_1_1 = _f.sent();
                        e_1 = { error: e_1_1 };
                        return [3 /*break*/, 8];
                    case 7:
                        try {
                            if (_c && !_c.done && (_e = _b.return)) _e.call(_b);
                        }
                        finally { if (e_1) throw e_1.error; }
                        return [7 /*endfinally*/];
                    case 8: return [2 /*return*/];
                }
            });
        });
    };
    ThumbnailRenderer.prototype.waitForAssetsToSettle = function () {
        return __awaiter(this, void 0, Promise, function () {
            var idleChecks, isLoading;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        idleChecks = 0;
                        _a.label = 1;
                    case 1:
                        if (!(idleChecks < assetSettleChecks)) return [3 /*break*/, 3];
                        return [4 /*yield*/, sleep(assetSettleIntervalMs)];
                    case 2:
                        _a.sent();
                        isLoading = this.app.assets.filter(function (asset) { return asset.loading; });
                        idleChecks = isLoading.length === 0 ? idleChecks + 1 : 0;
                        return [3 /*break*/, 1];
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    ThumbnailRenderer.prototype.frame = function (entity, view) {
        var _a, _b;
        entity.syncHierarchy();
        var meshInstances = (_b = (_a = entity.model) === null || _a === void 0 ? void 0 : _a.meshInstances) !== null && _b !== void 0 ? _b : [];
        if (meshInstances.length === 0) {
            throw new Error("\"".concat(entity.name, "\" has no mesh instances to frame"));
        }
        var aabb = new pc.BoundingBox();
        meshInstances.forEach(function (meshInstance, index) {
            return index === 0 ? aabb.copy(meshInstance.aabb) : aabb.add(meshInstance.aabb);
        });
        var camera = this.camera;
        var direction = viewDirection(view);
        var distance = fitDistance(aabb, direction, camera.fov, view.margin);
        var radius = aabb.halfExtents.length();
        camera.entity.setPosition(direction.clone().mulScalar(distance).add(aabb.center));
        camera.entity.lookAt(aabb.center);
        camera.nearClip = Math.max((distance - radius) / 2, 0.001);
        camera.farClip = distance + radius * 2;
    };
    return ThumbnailRenderer;
}());
export { ThumbnailRenderer };
